import moment, { Moment } from "moment";
import { useUpdateDetailsPanelsData } from "PFApp/booking/components/details_panel/hooks/use_update_details_panels_data";
import { useBookingApprovalsInvalidate } from "PFCore/hooks/queries/bookings/approvals/use_booking_approvals_invalidate";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Booking, BookingCategory, Profile, Template } from "PFTypes";
import { PropsWithChildren, useMemo, useState } from "react";

import { getRoleJobCodeDisplayAs } from "../../use_role_job_code";
import { ProfileBookingsContext } from "./profile_bookings_context";
import { filterBookings } from "./profile_bookings_provider.utils";
import { useProfileBookings } from "./use_profile_bookings";

type ProfileBookingsConfig = { tooltip?: { hideActivityLink?: boolean } };

type ProfileBookingsProviderProps = {
  templates: Template[];
  profile: Profile;
  config?: ProfileBookingsConfig;
  bookingCategories: BookingCategory[];
  isSearchEnabled?: boolean;
};

export const ProfileBookingsProvider = ({
  children,
  templates,
  config,
  profile,
  bookingCategories,
  isSearchEnabled
}: PropsWithChildren<ProfileBookingsProviderProps>) => {
  const { invalidate: invalidateBookingApprovals } = useBookingApprovalsInvalidate();

  const [selectedBooking, selectBooking] = useState<Booking | null>(null);
  const [search, setSearch] = useState("");
  const { formatISODate } = useDateFormatter();
  const [dateRange, setDateRange] = useState<{ min: string; max: string } | undefined>();
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [fetchAllEnabled, setFetchAllEnabled] = useState(false);

  const { bookings, refreshBookings } = useProfileBookings({
    profile,
    dateRange,
    fetchAllEnabled
  });

  const jobCodeDisplayAs = useMemo(() => getRoleJobCodeDisplayAs(templates), [templates]);

  const handleDateRangeChange = (min: Moment, max: Moment, month: Moment) => {
    setDateRange({
      min: formatISODate(min),
      max: formatISODate(max)
    });
    setCurrentMonth(month);
  };

  useUpdateDetailsPanelsData({
    data: {
      bookingDetails: {
        onDeleted: async () => {
          selectBooking(null);
          await invalidateBookingApprovals();
          await refreshBookings();
        },
        onEdited: async () => {
          await invalidateBookingApprovals();
          await refreshBookings();
        }
      }
    },
    dependencies: []
  });

  return (
    <ProfileBookingsContext.Provider
      value={{
        selectedBooking,
        bookings: isSearchEnabled ? filterBookings(bookings, search, formatISODate(currentMonth)) : bookings,
        bookingCategories,
        config,
        jobCodeDisplayAs,
        refreshBookings,
        selectBooking: (booking: Booking) => selectBooking(booking),
        handleDateRangeChange,
        fetchAllBookings: () => setFetchAllEnabled(true),
        calendarSearch: {
          search,
          setSearch,
          isSearchEnabled: !!isSearchEnabled
        }
      }}
    >
      {children}
    </ProfileBookingsContext.Provider>
  );
};
